import {
  normalizePageTabValue,
  type PageTabValue,
} from '@constants/pageTabs';
import type { IndicesTabValue } from '@constants/indicesTabs';

/** Reads the auth token without mutating the URL; embedding requires it to remain visible. */
const normalizeQueryParamValue = (
  value: string | null | undefined,
): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  const lowered = trimmed.toLowerCase();
  return lowered === 'null' || lowered === 'undefined' || trimmed === '""' || trimmed === "''"
    ? undefined
    : trimmed;
};

export const getTokenFromSearch = (search: string): string | undefined => {
  return normalizeQueryParamValue(new URLSearchParams(search).get('token'));
};

export interface IframeQueryParams {
  ticker?: string;
  mkt?: string;
  tab?: string;
  token?: string;
}

export interface InstrumentQueryParams {
  ticker: string;
  mkt: string;
}

const getTrimmedParam = (params: URLSearchParams, key: keyof IframeQueryParams) => {
  return normalizeQueryParamValue(params.get(key));
};

/**
 * Canonicalizes the parsed `mkt` value for validation/API use without ever
 * rewriting the browser URL. A present value is uppercased (e.g. `jp` -> `JP`,
 * ` Us ` -> `US`); blank/null/undefined stays `undefined`. Unsupported values
 * such as `CN` are preserved so validation can reject them.
 */
const normalizeMarketParam = (value: string | undefined): string | undefined =>
  value ? value.toUpperCase() : undefined;

/** Common iframe context accepted by every widget route. */
export const getIframeQueryParamsFromSearch = (search: string): IframeQueryParams => {
  const params = new URLSearchParams(search);
  return {
    ticker: getTrimmedParam(params, 'ticker'),
    mkt: normalizeMarketParam(getTrimmedParam(params, 'mkt')),
    tab: getTrimmedParam(params, 'tab'),
    token: getTrimmedParam(params, 'token'),
  };
};

/** Adds/overrides the `token` query param so the authenticated URL stays shareable/embeddable. */
export const appendTokenParam = (search: string, token: string): string => {
  const trimmed = token.trim();
  if (!trimmed) return search;

  const params = new URLSearchParams(search);
  params.set('token', trimmed);
  return `?${params.toString()}`;
};

export const appendMissingInstrumentParams = (
  search: string,
  instrument: InstrumentQueryParams,
) => {
  const params = new URLSearchParams(search);
  const hasTicker = Boolean(params.get('ticker')?.trim());
  const hasMkt = Boolean(params.get('mkt')?.trim());

  if (!hasTicker) params.set('ticker', instrument.ticker);
  if (!hasMkt) params.set('mkt', instrument.mkt);

  return `?${params.toString()}`;
};

export const appendPageTabParam = (
  search: string,
  tab: PageTabValue,
): string => {
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return `?${params.toString()}`;
};

/** Replaces only the standalone indices tab while retaining every caller parameter. */
export const appendIndicesTabParam = (
  search: string,
  tab: IndicesTabValue,
): string => {
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return `?${params.toString()}`;
};

export const appendRequiredIframeParams = (
  search: string,
  instrument: InstrumentQueryParams,
  tab: PageTabValue,
): string => {
  const params = new URLSearchParams(search);
  const hasTicker = Boolean(params.get('ticker')?.trim());
  const hasMkt = Boolean(params.get('mkt')?.trim());

  if (!hasTicker) params.set('ticker', instrument.ticker);
  if (!hasMkt) params.set('mkt', instrument.mkt);
  if (params.get('tab') !== tab) params.set('tab', tab);

  return `?${params.toString()}`;
};

export const appendPostLoginIframeParams = (
  search: string,
  token: string,
  instrument: InstrumentQueryParams | null,
  fallbackTab: PageTabValue,
): string => {
  const params = new URLSearchParams(search);
  const trimmedToken = token.trim();
  if (trimmedToken) params.set('token', trimmedToken);

  const hasTicker = Boolean(normalizeQueryParamValue(params.get('ticker')));
  const hasMkt = Boolean(normalizeQueryParamValue(params.get('mkt')));
  if (instrument && !hasTicker) params.set('ticker', instrument.ticker);
  if (instrument && !hasMkt) params.set('mkt', instrument.mkt);

  if (!normalizePageTabValue(normalizeQueryParamValue(params.get('tab')))) {
    params.set('tab', fallbackTab);
  }

  return `?${params.toString()}`;
};
